import React, { useCallback, useContext, createContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  buildLocalizedPath,
  i18nLanguageToUrlPrefix,
  parseLocaleFromPathname,
  switchLocaleInPathname,
} from "../utils/localeUrl";

const LocalePathContext = createContext(null);

export function LocalePathProvider({ localePrefix, children }) {
  const value = React.useMemo(() => {
    const prefix = localePrefix || null;

    const toAppPathname = (pathname) => parseLocaleFromPathname(pathname).pathnameWithoutLocale;

    const localizePath = (pathname) => {
      if (!pathname || typeof pathname !== "string") return pathname;
      if (!prefix) return pathname;
      if (/^https?:\/\//i.test(pathname)) return pathname;
      const [pathPart, queryPart] = pathname.split("?");
      const localized = buildLocalizedPath(pathPart, prefix);
      return queryPart ? `${localized}?${queryPart}` : localized;
    };

    const switchLocalePath = (pathname, targetPrefix) =>
      switchLocaleInPathname(pathname, targetPrefix);

    const localizePathFromI18n = (pathname, language) =>
      buildLocalizedPath(pathname, i18nLanguageToUrlPrefix(language));

    return {
      localePrefix: prefix,
      toAppPathname,
      localizePath,
      switchLocalePath,
      localizePathFromI18n,
    };
  }, [localePrefix]);

  return <LocalePathContext.Provider value={value}>{children}</LocalePathContext.Provider>;
}

export function useLocalePath() {
  const ctx = useContext(LocalePathContext);
  if (!ctx) {
    return {
      localePrefix: null,
      toAppPathname: (pathname) => pathname,
      localizePath: (pathname) => pathname,
      switchLocalePath: (pathname, targetPrefix) =>
        switchLocaleInPathname(pathname, targetPrefix),
      localizePathFromI18n: (pathname, language) =>
        buildLocalizedPath(pathname, i18nLanguageToUrlPrefix(language)),
    };
  }
  return ctx;
}

export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const { localizePath } = useLocalePath();
  return useCallback(
    (to, options) => {
      if (typeof to === "string") {
        navigate(localizePath(to), options);
        return;
      }
      navigate(to, options);
    },
    [navigate, localizePath]
  );
}
